import { useTranslation } from '../../../shared/i18n';
import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Space, Tag, Modal, Form, Input, message, Popconfirm, Select, Empty, Alert, Tooltip } from 'antd';
import { PlusOutlined, DeleteOutlined, EditOutlined, ArrowLeftOutlined, ReloadOutlined, SwapRightOutlined } from '@ant-design/icons';
import { useSearchParams, useNavigate } from 'react-router-dom';
import webmailService from '../../services/webmailService';

import { userPath } from '../../../shared/config/paths';
import TagsInput, { isEmail } from '../../../shared/components/TagsInput';

const Aliases = () => {
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const domainIdFromUrl = searchParams.get('domain_id');
    const domainNameFromUrl = searchParams.get('domain_name');

    const [aliases, setAliases] = useState([]);
    const [usage, setUsage] = useState({ current: 0, max: 0 });
    const [domains, setDomains] = useState([]);
    const [selectedDomain, setSelectedDomain] = useState(domainIdFromUrl ? Number(domainIdFromUrl) : null);
    const [selectedDomainName, setSelectedDomainName] = useState(domainNameFromUrl || '');
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [error, setError] = useState(null);
    const [sourceDomains, setSourceDomains] = useState([]);
    const [sourceDomain, setSourceDomain] = useState('');
    const [editing, setEditing] = useState(null); // 正在编辑的别名;null = 新建
    const [form] = Form.useForm();

    const fetchDomains = async () => {
        try {
            const res = await webmailService.getDomains();
            if (res.code === 0 || res.success) {
                const list = res.data || [];
                setDomains(list);
                if (!selectedDomain && list.length > 0) {
                    setSelectedDomain(list[0].local_id);
                    setSelectedDomainName(list[0].name);
                }
            }
        } catch (e) {
            message.error(t('Failed to retrieve domains'));
        }
    };

    const fetchAliases = async () => {
        if (!selectedDomain) return;
        setLoading(true);
        setError(null);
        try {
            const res = await webmailService.getAliases({ domain_id: selectedDomain });
            if (res.code === 0 || res.success) {
                const data = res.data || {};
                setAliases(data.aliases || []);
                setUsage(data.usage || { current: 0, max: 0 });
                if (data.domain_name) setSelectedDomainName(data.domain_name);
                setSourceDomains(Array.isArray(data.source_domains) ? data.source_domains : []);
                setSourceDomain(data.domain_name || '');
            } else {
                const msg = res.message || res.error || t('Failed to retrieve aliases');
                setError(msg);
                setAliases([]);
            }
        } catch (e) {
            setError(t('Failed to retrieve aliases'));
            setAliases([]);
        }
        setLoading(false);
    };

    useEffect(() => {
        fetchDomains();
    }, []);

    useEffect(() => {
        if (selectedDomain) {
            fetchAliases();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedDomain]);

    const handleDomainChange = (domainId) => {
        setSelectedDomain(domainId);
        const domain = domains.find(d => d.local_id === domainId);
        setSelectedDomainName(domain?.name || '');
    };

    const handleAdd = () => {
        setEditing(null);
        form.resetFields();
        setModalVisible(true);
    };

    const handleEdit = (record) => {
        setEditing(record);
        form.setFieldsValue({ destination: record.destination });
        setModalVisible(true);
    };

    const handleResend = async (record, address) => {
        const res = await webmailService.resendAliasVerify({ domain_id: selectedDomain, alias_id: record.id, address });
        if (res.code === 0 || res.success) {
            message.success(t('Confirmation email sent to {{address}}', { address }));
        } else {
            message.error(res.message || res.error || t('Send failed'));
        }
    };

    // 保存后提示哪些外部地址要等对方确认
    const notifyPending = (targets) => {
        const pending = (targets || []).filter(x => x.status === 'pending').map(x => x.address);
        if (pending.length) {
            message.info(t('Confirmation email sent to {{list}}. Forwarding starts once confirmed.', { list: pending.join(', ') }), 5);
        }
    };

    const handleDelete = async (record) => {
        try {
            const res = await webmailService.deleteAlias({ domain_id: selectedDomain, alias_id: record.id });
            if (res.code === 0 || res.success) {
                message.success(t('Alias deleted'));
                fetchAliases();
            } else {
                message.error(res.message || res.error || t('Deletion failed'));
            }
        } catch (e) {
            message.error(t('Deletion failed'));
        }
    };

    const handleSubmit = async (values) => {
        setSubmitting(true);
        try {
            const res = editing
                ? await webmailService.updateAlias({ domain_id: selectedDomain, alias_id: editing.id, destination: values.destination })
                : await webmailService.createAlias({
                    domain_id: selectedDomain,
                    username: values.username,
                    source_domain: sourceDomain || selectedDomainName,
                    destination: values.destination,
                });
            if (res.code === 0 || res.success) {
                message.success(editing ? t('Saved') : t("Alias created: {{source}}", { source: res.data?.source || '' }));
                notifyPending(res.data?.targets);
                setModalVisible(false);
                fetchAliases();
            } else {
                message.error(res.message || res.error || t('Creation failed'));
            }
        } catch (e) {
            message.error(t('Operation failed'));
        } finally {
            setSubmitting(false);
        }
    };

    const usageLimited = usage.max >= 0;
    const atLimit = usageLimited && usage.current >= usage.max;

    const columns = [
        {
            title: t('Alias address (source)'),
            dataIndex: 'source',
            render: (source, record) => (
                <Space>
                    <strong>{source}</strong>
                    {record.active === 0 && ((record.targets || []).some(x => x.status === 'pending')
                        ? <Tag color="orange">{t("Pending")}</Tag>
                        : <Tag color="red">{t("Disabled")}</Tag>)}
                </Space>
            ),
        },
        {
            title: '',
            width: 40,
            align: 'center',
            render: () => <SwapRightOutlined style={{ color: '#999' }} />,
        },
        {
            title: t('Forward to (destination)'),
            dataIndex: 'destination',
            render: (destination, record) => {
                const targets = record.targets || String(destination || '').split(',').filter(Boolean).map(a => ({ address: a.trim(), status: 'verified' }));
                return (
                    <Space size={4} wrap>
                        {targets.map((x) => x.status === 'pending' ? (
                            <Tooltip key={x.address} title={t('Forwarding starts after the recipient confirms. Click to resend the email.')}>
                                <Tag color="orange" style={{ cursor: 'pointer' }} onClick={() => handleResend(record, x.address)}>
                                    {x.address} · {t('Pending')}
                                </Tag>
                            </Tooltip>
                        ) : (
                            <Tag key={x.address} color="blue">{x.address}</Tag>
                        ))}
                    </Space>
                );
            },
        },
        {
            title: t('Actions'),
            width: 120,
            render: (_, record) => (
                <Space size={4}>
                <Button size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
                <Popconfirm
                    title={t("Delete this alias?")}
                    description={t("Email sent to this alias address will no longer be forwarded.")}
                    okText={t("Delete")}
                    cancelText={t("Cancel")}
                    onConfirm={() => handleDelete(record)}
                >
                    <Button size="small" danger icon={<DeleteOutlined />} />
                </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <Card
            title={
                <Space>
                    {domainIdFromUrl && (
                        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(userPath('/webmail/domains'))} />
                    )}
                    <span>{t("Email aliases")} {selectedDomainName && `- ${selectedDomainName}`}</span>
                    {selectedDomain && (
                        <Tag color={atLimit ? 'red' : 'blue'}>
                            {t('Used {{current}} / {{max}}', { current: usage.current, max: usageLimited ? usage.max : t('Unlimited') })}
                        </Tag>
                    )}
                </Space>
            }
            extra={
                <Space>
                    <Select
                        style={{ width: 200 }}
                        value={selectedDomain}
                        onChange={handleDomainChange}
                        placeholder={t("Select a domain")}
                        showSearch
                        optionFilterProp="children"
                    >
                        {domains.map(d => (
                            <Select.Option key={d.local_id} value={d.local_id}>
                                {d.name}
                            </Select.Option>
                        ))}
                    </Select>
                    <Button icon={<ReloadOutlined />} onClick={fetchAliases} />
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={handleAdd}
                        disabled={!selectedDomain || atLimit}
                    >
                        {t("Add alias")}
                    </Button>
                </Space>
            }
        >
            {atLimit && (
                <Alert
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message={t("You have reached your plan's alias limit ({{current}}/{{max}}). Please upgrade your plan to add more.", { current: usage.current, max: usage.max })}
                />
            )}
            {error && (
                <Alert type="error" showIcon style={{ marginBottom: 16 }} message={error} />
            )}
            <Table
                columns={columns}
                dataSource={aliases}
                rowKey="id"
                loading={loading}
                scroll={{ x: 'max-content' }}
                locale={{ emptyText: <Empty description={selectedDomain ? t('No aliases yet. Click "Add alias" in the top right to create a forwarding address.') : t('Please select a domain first.')} /> }}
                pagination={{ pageSize: 10 }}
            />

            <Modal
                title={editing ? editing.source : t("Add alias")}
                open={modalVisible}
                onCancel={() => setModalVisible(false)}
                footer={null}
                width={520}
            >
                <Form form={form} layout="vertical" onFinish={handleSubmit}>
                    {!editing && <Form.Item
                        name="username"
                        label={t("Alias address (source)")}
                        rules={[
                            { required: true, message: t('Please enter an alias address prefix') },
                            { pattern: /^[a-zA-Z0-9][a-zA-Z0-9._%+-]*$/, message: t('Invalid format (must start with a letter or number)') },
                        ]}
                        extra={selectedDomainName && t("Full address: [prefix]@{{domain}}", { domain: sourceDomain || selectedDomainName })}
                    >
                        <Input
                            placeholder={t("E.g. sales")}
                            addonAfter={sourceDomains.length > 1 ? (
                                <Select value={sourceDomain || selectedDomainName} onChange={setSourceDomain} style={{ minWidth: 160 }}
                                    options={sourceDomains.map(d => ({ value: d, label: `@${d}` }))} />
                            ) : (selectedDomainName ? `@${selectedDomainName}` : undefined)}
                        />
                    </Form.Item>}
                    <Form.Item
                        name="destination"
                        label={t("Forward to (destination email)")}
                        rules={[{ required: true, message: t('Enter the destination email address') }]}
                        extra={t("External addresses start receiving forwards after they confirm via email.")}
                    >
                        <TagsInput format="comma" placeholder={t("e.g., user@example.com")} validate={(v) => (isEmail(v) ? null : t('Invalid email address format'))} />
                    </Form.Item>
                    <Form.Item>
                        <Space>
                            <Button type="primary" htmlType="submit" loading={submitting}>{t("Save")}</Button>
                            <Button onClick={() => setModalVisible(false)} disabled={submitting}>{t("Cancel")}</Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>
        </Card>
    );
};

export default Aliases;
